import { AppPlatform, AppStoreType, type ReleaseStepType } from '@/types';
import type { Destination } from '@/types/destinations';
import type { WorkflowRunStepRunGraph } from '@/types/workflowRunStepRunGraph';
import workflowsPlatformAndroid from '../../assets/workflows-platform-android.svg';
import workflowsPlatformIos from '../../assets/workflows-platform-ios.svg';
import workflowsPlatformReactNative from '../../assets/workflows-platform-react-native.svg';

export interface TestDataConfig {
    stepConfigId: string;
    stepName: string;
    archetype: ReleaseStepType;
    destination: Destination;
}

export const createDestination = (
    stepConfigId: string,
    name: string,
    appName: string,
    platform: AppPlatform,
    appStoreType: AppStoreType,
    iconURL: string,
): Destination => ({
    stepConfigId,
    name,
    appName,
    platform,
    appStoreType,
    iconURL,
});

export const DEMO_DESTINATIONS = {
    appolloIos: createDestination(
        'dest-appollo-ios',
        'Appollo iOS',
        'Appollo',
        AppPlatform.Ios,
        AppStoreType.Apple,
        'https://runway-img.s3.us-east-2.amazonaws.com/app-demo-mercury.png',
    ),
    appolloAndroid: createDestination(
        'dest-appollo-android',
        'Appollo Android',
        'Appollo',
        AppPlatform.Android,
        AppStoreType.Google,
        'https://runway-img.s3.us-east-2.amazonaws.com/app-demo-mercury.png',
    ),
    appolloReactNativeOta: createDestination(
        'dest-appollo-react-native-ota',
        'Appollo OTA',
        'Appollo',
        AppPlatform.ReactNativeOTA,
        AppStoreType.None,
        'https://runway-img.s3.us-east-2.amazonaws.com/app-demo-mercury.png',
    ),
    geminiIos: createDestination(
        'dest-ios',
        'Gemini iOS',
        'Gemini',
        AppPlatform.Ios,
        AppStoreType.Apple,
        'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-ios.png',
    ),
    geminiAndroid: createDestination(
        'dest-android',
        'Gemini Android',
        'Gemini',
        AppPlatform.Android,
        AppStoreType.Google,
        'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-android.png',
    ),
    voyagerIos: createDestination(
        'dest-voyager-ios',
        'Voyager iOS',
        'Voyager',
        AppPlatform.Ios,
        AppStoreType.Apple,
        workflowsPlatformIos,
    ),
    voyagerAndroid: createDestination(
        'dest-voyager-android',
        'Voyager Android',
        'Voyager',
        AppPlatform.Android,
        AppStoreType.Google,
        workflowsPlatformAndroid,
    ),
    voyagerExpo: createDestination(
        'dest-voyager-expo',
        'Voyager Expo',
        'Voyager',
        AppPlatform.ExpoEAS,
        AppStoreType.ExpoEAS,
        workflowsPlatformReactNative,
    ),
} as const;

const WHITE_LABEL_APP_NAMES = [
    'Capsule',
    'Gimbal',
    'Launch',
    'Manifold',
    'Moon',
    'Orbit',
    'Pulsar',
    'Radiator',
    'Relay',
    'Shield',
    'Telemetry',
    'Thruster',
] as const;

export const createWhiteLabelDestinations = (): Destination[] => {
    return WHITE_LABEL_APP_NAMES.map((name) =>
        createDestination(
            `dest-${name.toLowerCase()}-ios`,
            name,
            'Appollo',
            AppPlatform.Ios,
            AppStoreType.Apple,
            `https://runway-img.s3.us-east-2.amazonaws.com/app-demo-${name.toLowerCase()}.png`,
        ),
    );
};

export const getAllDestinations = (): Destination[] => {
    return Object.values(DEMO_DESTINATIONS);
};

export const getAllDestinationIds = (): string[] => {
    return Object.values(DEMO_DESTINATIONS).map((d) => d.stepConfigId);
};

export const getDestinationsByPlatform = (platform: AppPlatform): Destination[] => {
    return Object.values(DEMO_DESTINATIONS).filter((d) => d.platform === platform);
};

export const createStepRunBase = (
    stepConfigId: string,
    stepRunId: string,
    archetype: ReleaseStepType,
    destination: Destination,
) => ({
    id: stepRunId,
    stepId: stepConfigId,
    type: archetype,
    displayName: destination.name,
    destinations: [destination],
    stepConfigSnapshot: {
        settings: null,
        integrationInstallationIds: [],
    },
});

export const getDestinationsByStepConfigIds = (stepConfigIds: string[]): Destination[] => {
    const allDestinations = getAllDestinations();
    return allDestinations.filter((dest) => stepConfigIds.includes(dest.stepConfigId));
};

export const collectStepsByArchetypeFromGraph = (
    graph: WorkflowRunStepRunGraph,
    archetype: ReleaseStepType,
): Array<{ stepName: string; stepConfigId: string; stepRunId: string }> => {
    const results: Array<{ stepName: string; stepConfigId: string; stepRunId: string }> = [];

    for (const stepGroup of graph.stepGroups) {
        for (const archetypeGroup of stepGroup.stepArchetypeGroups) {
            if (archetypeGroup.archetype === archetype) {
                const steps = archetypeGroup.stepRunStatusDatum;
                for (const step of steps) {
                    results.push({
                        stepName: step.name,
                        stepConfigId: step.stepConfigId!,
                        stepRunId: step.stepRunId!,
                    });
                }
            }
        }
    }

    return results;
};

export const buildStepConfigToDestinationsMap = <T extends { stepGroups: any[] }>(
    stepGraph: T,
): Record<string, string[]> => {
    return stepGraph.stepGroups.reduce(
        (acc, stepGroup) => {
            stepGroup.archetypeGroups.forEach((archetypeGroup: any) => {
                archetypeGroup.steps.forEach((step: any) => {
                    acc[step.stepConfigId] = step.destinationStepConfigIds;
                });
            });
            return acc;
        },
        {} as Record<string, string[]>,
    );
};
